import { useEffect, useMemo, useState, useCallback } from 'react';
import { Tabs, Table, Empty, InputNumber, Tooltip, Button, Select, Input } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { loadManualChargeLookupsSafe } from '../../masters/manualCharges/manualChargeApi';
import {
  selectGridFabrications, getFabCount, setFabCount, round2, round3, formatMoney,
  breakdownValueAddedCharges, resolveSheetCostMode, reconcileProformaTotals
} from './proformaHelpers';
import { otherChargesApi } from '../../otherCharges/otherChargesApi';
import { buildPreviewPayload } from '../../otherCharges/otherChargesHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage 6Q — Proforma bottom panel (GlassMaster-style "Other Charges /
// Summary" tabs under the line grid).
//
//  • Process / Fabrication — live, with an EDITABLE per-document price.
//  • Other Charges — amounts come from the REAL Other-Charges RULE ENGINE
//    (otherChargesApi.preview): conditions, area slabs, charge base and GST
//    are evaluated server-side against the proforma's priced lines. We never
//    re-implement the math here, so the slab/condition logic always applies.
//  • Summary — the live document totals from the preview engine.
// ════════════════════════════════════════════════════════════════════

function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }

// Stable empty object so `header.otherChargeValues || EMPTY` keeps a constant
// reference when unset — otherwise a fresh {} each render defeats the memoised
// Other-Charges table and re-renders all 39 InputNumbers on every keystroke.
const EMPTY_OBJ = Object.freeze({});

function fabCode(f) {
  return f.code || f.value || f.fabricationCode || f.abbr || f.fabRefName
    || (f.fabId != null ? `F${f.fabId}` : null);
}
// A change-revision line that bills nothing — a struck-out original or a
// waived/non-chargeable cancelled cut-piece line. Mirrors computeLeafRow's
// `coZero` in proformaHelpers.js so the fabrication summary excludes exactly the
// lines whose fabricationAmount the engine already zeroed. Inert for LIVE lines.
function coZeroLine(l) {
  const s = String((l && l.lineState) || '').toUpperCase();
  return s === 'STRUCK' || (s === 'CANCELLED' && l.chargeable === false);
}
function fabMasterRate(f) {
  return num(f.rate != null ? f.rate
    : f.defaultRate != null ? f.defaultRate
      : f.ratePerUnit != null ? f.ratePerUnit : f.defaultPrice);
}
function entryRate(lines, code) {
  for (const l of (Array.isArray(lines) ? lines : [])) {
    const e = (l.fabricationDetails && Array.isArray(l.fabricationDetails.entries) ? l.fabricationDetails.entries : [])
      .find((x) => x && x.code === code);
    if (e && num(e.rate) > 0) return num(e.rate);
  }
  return null;
}

// Map a priced proforma line → the Other-Charges engine line shape.
function toEngineLine(l) {
  // SHEET scrap → Other-Charges base (CLAUDE.md rule #9). A SHEET carries no
  // product basic, but the scrap value it bills IS its basic for charge purposes,
  // so percentage charges compound on it. A SHEET scrap pseudo-line sets
  // _scrapBasic; use it as basicAmount. Mirrors backend proforma.service ocBases.
  const scrapBasic = num(l._scrapBasic);
  // The Other-Charges base for a billed leaf is its FULL pre-OC value —
  //   basicAmount + fabricationAmount + valueAddedAmount
  // i.e. the same discountBase the taxable amount is built on — NOT the glass
  // basic alone. A percentage / running-total charge (Handling / Insurance /
  // Exp Delv) must compound on the fabrication + value-add (edgework / bevelling /
  // polish) the customer is billed for, otherwise Handling 3% bills 3% of glass
  // only and undercharges by the process value. This is the EXACT rule the backend
  // applies in proforma.service ocBases (leaf ? basic + fab + valueAdd : 0), so the
  // live editor figure now matches the saved / printed PDF (CLAUDE.md rule 4).
  // A SHEET scrap pseudo-line has no fabrication / value-add — its scrap value IS
  // its basic (scrapBasic wins), unchanged.
  const fullBasic = num(l.basicAmount) + num(l.fabricationAmount) + num(l.valueAddedAmount);
  return {
    type: l.type || null,
    widthMm: num(l.actualWidthMm), heightMm: num(l.actualHeightMm), qty: num(l.qty),
    sqm: num(l.actualSqm), chargeableSqm: num(l.chargeSqm),
    basicRate: num(l.salesRate),
    basicAmount: scrapBasic > 0 ? scrapBasic : fullBasic,
    thicknessMm: num(l.thicknessMm),
    materialTypeCode: l.materialTypeCode || null, glassTypeCode: l.glassTypeCode || null,
    categoryCode: l.categoryCode || null, itemName: l.itemDesc || null,
    // Group scrap for Jumbo/Shape/Template on a glass part (attached upstream).
    sheetScrapForPartCharge: num(l._sheetScrapForPartCharge),
    // Per-ply SHAPE / TEMPLATE opt-in (LAMI/DGU) — read from the ply's own
    // nesting_config (or a flat flag) so the live preview bills the Shape / Template
    // charge on it, matching the backend save path.
    isShape:    (l.nestingConfig && l.nestingConfig.shape === true) || l.isShape === true || l.shape === true,
    isTemplate: (l.nestingConfig && l.nestingConfig.template === true) || l.isTemplate === true || l.template === true
  };
}

export default function ProformaBottomPanel({ lines, setLines, header, setHeader, dropdowns, pricedLines, totals, supplyType, readOnly, onOtherChargesChange }) {
  const t = totals || {};
  const ro = !!readOnly;
  // Stage 6CM.2 — Company Defaults Other-Charges gates.
  const autoApplyDisabled = header && header.autoApplyOtherCharges === false;
  const overrideLocked    = header && header.allowRuntimeOtherChargeOverride === false;
  const fabRates = (header && header.fabRates) || {};
  // Per-proforma Other-Charge value overrides. Defaults come from the master
  // (rule.ruleValue); the user may customise per document. An entry is written
  // only when edited, so untouched rules keep the master default (incl. slabs).
  // Memoised so a keystroke in the GRID (which re-renders this panel via new
  // pricedLines/totals props) does NOT churn the Other-Charges table identity.
  const chargeValues = useMemo(() => (header && header.otherChargeValues) || EMPTY_OBJ, [header]);
  const setChargeValue = useCallback((id, val) => {
    const v = Math.max(0, num(val));
    setHeader((h) => ({ ...h, otherChargeValues: { ...(h.otherChargeValues || {}), [id]: v } }));
  }, [setHeader]);


  // ── Fabrication summary (live qty + editable price) ──
  // QTY is the COMMERCIAL fabrication count: count × line-qty × glass-layers. For
  // a LAMI / DGU / LAMI+DGU assembly a hole/cut-out is physically done on EVERY
  // glass ply, so it is charged per layer — the engine's layer multiplier
  // (parentFabLayers for an assembly built from child panes, fabLayers for a flat
  // single-row assembly) is applied here so the summary matches the billed
  // fabricationAmount. A plain line multiplies by 1. We read from the PRICED lines
  // (preview), which carry both the raw fab counts (…l) and the layer fields.
  const fabSourceLines = (Array.isArray(pricedLines) && pricedLines.length) ? pricedLines : lines;
  const fabLayerMultiplier = (l) => {
    const lc = num(l && (l.parentFabLayers != null ? l.parentFabLayers : l.fabLayers));
    return lc > 0 ? lc : 1;
  };
  const fabRows = useMemo(() => {
    const active = selectGridFabrications(dropdowns && dropdowns.fabrications);
    const rows = [];
    for (const f of active) {
      const code = fabCode(f);
      if (!code) continue;
      const name = f.fabName || f.name || f.label || f.fabRefName || code;
      const price = fabRates[code] != null ? num(fabRates[code]) : (entryRate(fabSourceLines, code) != null ? entryRate(fabSourceLines, code) : fabMasterRate(f));
      let qty = 0;
      for (const l of (Array.isArray(fabSourceLines) ? fabSourceLines : [])) {
        if (coZeroLine(l)) continue;   // struck / waived lines bill no fabrication
        qty += getFabCount(l, code) * num(l.qty) * fabLayerMultiplier(l);
      }
      qty = round2(qty);
      rows.push({ key: code, code, name, price, qty, total: round2(qty * price) });
    }
    return rows;
  }, [dropdowns, fabSourceLines, fabRates]);
  const fabTotal = useMemo(() => round2(fabRows.reduce((s, r) => s + num(r.total), 0)), [fabRows]);

  const setFabPrice = (code, val) => {
    const rate = Math.max(0, num(val));
    setHeader((h) => ({ ...h, fabRates: { ...(h.fabRates || {}), [code]: rate } }));
    setLines((rows) => rows.map((l) => {
      const cnt = getFabCount(l, code);
      if (cnt <= 0) return l;
      return { ...l, fabricationDetails: setFabCount(l, code, rate, cnt) };
    }));
  };

  const fabColumns = [
    { title: 'PRC', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 72, align: 'right', render: (v) => (num(v) ? num(v).toLocaleString('en-IN') : '0') },
    {
      title: 'Price', key: 'price', width: 132, align: 'right', onCell: () => ({ className: 'pfg-bp-edit' }),
      render: (_v, r) => (
        <InputNumber size="small" min={0} controls={false} variant="borderless" prefix="₹" disabled={ro}
          value={r.price} onChange={(val) => setFabPrice(r.code, val)} className="pfg-bp-num" />
      )
    },
    { title: 'Total', dataIndex: 'total', key: 'total', width: 118, align: 'right', render: (v) => num(v).toFixed(2) }
  ];

  // ── Value Added Services (process #tag charges, read-only) ──
  // Itemised from each priced line's processPricing[] — the same charges that
  // roll into the document's "Value Add (Process)" total. The rate comes from the
  // item-desc #tag (or thickness rate), so it is shown read-only here; edit it by
  // editing the line's #tag in the grid.
  const vas = useMemo(
    () => breakdownValueAddedCharges(pricedLines, header && header.edgeworkDimensionBasis),
    [pricedLines, header]);
  const vasColumns = [
    { title: 'Process', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', width: 84, align: 'right', render: (v) => num(v).toFixed(2) },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 60, align: 'center', render: (v) => v || '—' },
    { title: 'Area', dataIndex: 'area', key: 'area', width: 84, align: 'right', render: (v) => num(v).toFixed(3) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 118, align: 'right', render: (v) => num(v).toFixed(2) }
  ];

  // ── Other-Charges master rules ──
  const [rules, setRules] = useState([]);
  const [rulesErr, setRulesErr] = useState(false);
  useEffect(() => {
    let alive = true;
    otherChargesApi.listRules({ isActive: true, limit: 200 })
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data && Array.isArray(data.rows) ? data.rows : []);
        setRules(list);
      })
      .catch(() => { if (alive) setRulesErr(true); });
    return () => { alive = false; };
  }, []);

  // Billed product leaves only (skip roll-up parents + SHEET sizes / GASFILL).
  //
  // BUSINESS RULE (CLAUDE.md #9) — a SHEET takes NO finished-glass PRODUCT logic
  // (no fabrication / value-add / polish), so the SHEET roll-up row is excluded
  // from `billed`. Its scrap value, however, IS its basic for CHARGE purposes and
  // is fed back in via `sheetScrapLines` below so Other Charges compound on scrap
  // (matching the backend). GASFILL is a true no-money component — fully excluded.
  const billed = useMemo(() => {
    const rows = Array.isArray(pricedLines) ? pricedLines : [];
    const parentSet = new Set(rows.filter((r) => r && r.parentRefUuid).map((r) => r.parentRefUuid));
    return rows.filter((l) => l
      && !parentSet.has(l.refUuid)
      && !coZeroLine(l)                                     // struck / waived → no OC base (mirrors backend isBilledLeaf)
      && !['SHEET', 'GASFILL'].includes(String(l.type || '').toUpperCase())
      && (num(l.qty) > 0 || num(l.basicAmount) > 0));
  }, [pricedLines]);

  // Part-shape scrap: for each SHEET group whose nesting checkbox is ON
  // (nestingConfig.includeScrapInPartCharge !== false, default ON), map every glass
  // PART descendant → the value it contributes to its Jumbo/Shape/Template charge
  // base, BY SHEET cost mode (mirrors the backend proforma.service ocBases exactly):
  //   • WASTAGE     → the group's flat scrap value.
  //   • SHEET_PRICE → sheetRate × the part's chargeable area in the document Price
  //                   UOM (SQM/SQFT, locked from Company Defaults). The engine adds
  //                   this on top of the part basic (= partRate × area), so the
  //                   effective base = (sheetRate + partRate) × area. scrapValue is 0
  //                   in this mode, so this is what makes SHEET_PRICE parts earn a
  //                   contribution. See docs/proforma-sheet-line-rule.md item 5.
  const sqmPriced = ['SQM', 'SQMT'].includes(String((header && header.priceUomCode) || '').toUpperCase());
  const partScrapByRefUuid = useMemo(() => {
    const rows = Array.isArray(pricedLines) ? pricedLines : [];
    const byRef = new Map(rows.map((r) => [r.refUuid, r]));
    const kids = new Map();
    for (const r of rows) {
      if (r && r.parentRefUuid) { const a = kids.get(r.parentRefUuid) || []; a.push(r.refUuid); kids.set(r.parentRefUuid, a); }
    }
    const NON_GLASS_PART = new Set(['SHEET', 'INTERLAYER', 'SPACER', 'GASFILL', 'HARDWARE', 'SERVICES', 'LAMI', 'DGU', 'LAMI+DGU', 'COMBO']);
    const out = new Map();
    for (const r of rows) {
      if (String(r.type || '').toUpperCase() !== 'SHEET') continue;
      const cfg = (r.nestingConfig && typeof r.nestingConfig === 'object') ? r.nestingConfig : {};
      if (cfg.includeScrapInPartCharge === false) continue;               // group opted out
      const sv = num(r.scrapValue);
      const mode = resolveSheetCostMode(cfg);
      // Sheet per-area rate. `nestingConfig.parentRate` is primary; on a priced
      // line where it did not survive, derive it from the sheet's own priced value
      // (parentPriceAmount = parentRate × sheet chargeable area) — mathematically
      // identical and always present in SHEET_PRICE mode.
      const sheetArea = sqmPriced ? num(r.chargeSqm) : num(r.chargeSqft);
      let sheetRate = num(cfg.parentRate);
      if (!(sheetRate > 0) && num(r.parentPriceAmount) > 0 && sheetArea > 0) {
        sheetRate = num(r.parentPriceAmount) / sheetArea;
      }
      if (mode !== 'SHEET_PRICE' && !(sv > 0)) continue;                  // nothing to distribute (WASTAGE with no scrap)
      const stack = [...(kids.get(r.refUuid) || [])];
      while (stack.length) {
        const ref = stack.pop();
        const node = byRef.get(ref);
        if (!node) continue;
        const childList = kids.get(ref) || [];
        const isLeaf = childList.length === 0;
        if (isLeaf && !NON_GLASS_PART.has(String(node.type || '').toUpperCase())) {
          const value = mode === 'SHEET_PRICE'
            ? round2(sheetRate * (sqmPriced ? num(node.chargeSqm) : num(node.chargeSqft)))
            : sv;
          if (value > 0) out.set(ref, value);
        }
        for (const c of childList) stack.push(c);
      }
    }
    return out;
  }, [pricedLines, sqmPriced]);

  // SHEET scrap pseudo-lines — one per SHEET master that bills scrap, carrying its
  // scrapValue as _scrapBasic so toEngineLine bills percentage Other Charges on it.
  // Mirrors the backend (proforma.service ocBases) so the on-screen Other Charges
  // match the PDF. (SHEET still gets no fabrication/value-add/polish.)
  const sheetScrapLines = useMemo(() => {
    const rows = Array.isArray(pricedLines) ? pricedLines : [];
    return rows
      .filter((l) => l && String(l.type || '').toUpperCase() === 'SHEET' && num(l.scrapValue) > 0)
      .map((l) => ({ ...l, _scrapBasic: num(l.scrapValue) }));
  }, [pricedLines]);

  // ── Real rule-engine evaluation (debounced) ──
  // The proforma calculator evaluates ALL active charge rules, not only the
  // auto-included ones. The /preview endpoint defaults to active+auto_include;
  // passing the active rule ids + includeInactive bypasses the auto_include gate
  // (scoped to those ids, so inactive rules are NOT pulled in). This is why a
  // configured-but-not-auto-included rule (e.g. JUMBO) now computes here.
  const ruleIds = useMemo(() => rules.map((r) => (r.id != null ? String(r.id) : null)).filter(Boolean), [rules]);
  const rulesKey = useMemo(() => ruleIds.join(','), [ruleIds]);
  // Customised values → engine runtime overrides (finalRate replaces the rule's
  // per-unit rate / percent). Only rules the user actually edited are sent, so
  // unedited rules compute from the master value exactly as before.
  // Stage 6CM.2 — when Company Defaults locks runtime overrides, the preview
  // ignores stored values too (the backend strips them on save — stay in sync).
  const runtimeOverrides = useMemo(() => (overrideLocked ? [] : rules
    .filter((r) => r.id != null && chargeValues[String(r.id)] != null && chargeValues[String(r.id)] !== '')
    .map((r) => ({ ruleId: String(r.id), finalRate: num(chargeValues[String(r.id)]) }))), [rules, chargeValues, overrideLocked]);
  const overridesKey = useMemo(() => JSON.stringify(runtimeOverrides), [runtimeOverrides]);
  const [engineRows, setEngineRows] = useState([]);
  const [engineErr, setEngineErr] = useState(false);
  // True once the OC engine has produced a result (success / empty / error /
  // disabled) — lets the Summary popup know the live OC figure is meaningful
  // and it can safely reconcile it against the persisted per-line OC.
  const [ocComputed, setOcComputed] = useState(false);
  const engineKey = useMemo(() => JSON.stringify([
    ...billed.map((l) => [
      l.actualWidthMm, l.actualHeightMm, l.qty, l.basicAmount, l.salesRate,
      // Fabrication + value-add feed the OC base (see toEngineLine), so a change to
      // either must re-run the preview — else Handling %/running-total would lag.
      l.fabricationAmount, l.valueAddedAmount,
      l.thicknessMm, l.materialTypeCode, l.glassTypeCode, l.categoryCode,
      // Per-ply SHAPE / TEMPLATE flags change which charges apply → re-run preview.
      (l.nestingConfig && l.nestingConfig.shape) === true, (l.nestingConfig && l.nestingConfig.template) === true,
      num(partScrapByRefUuid.get(l.refUuid))            // group scrap on this part
    ]),
    // scrap pseudo-lines change the OC base too
    ...sheetScrapLines.map((l) => ['scrap', l.actualSqm, l.chargeSqm, l._scrapBasic, l.thicknessMm, l.materialTypeCode])
  ]), [billed, sheetScrapLines, partScrapByRefUuid]);
  useEffect(() => {
    // Stage 6CM.2 — skip the auto-apply preview entirely when Company Defaults
    // disables auto Other Charges (no auto-applied rows are shown).
    if (autoApplyDisabled) { setEngineRows([]); setEngineErr(false); setOcComputed(true); return undefined; }
    if (!billed.length && !sheetScrapLines.length) { setEngineRows([]); setEngineErr(false); setOcComputed(true); return undefined; }
    let alive = true;
    const tid = setTimeout(() => {
      const opts = { sourceModule: 'PROFORMA', includeInactive: true };
      if (ruleIds.length) opts.ruleIds = ruleIds;
      if (runtimeOverrides.length) opts.runtimeOverrides = runtimeOverrides;
      // Include SHEET scrap pseudo-lines so percentage charges compound on scrap,
      // matching the backend OC base (proforma.service ocBases). Attach each glass
      // part's group scrap so Jumbo/Shape/Template bill on part basic + scrap.
      const engineLines = [
        ...billed.map((l) => {
          const ps = num(partScrapByRefUuid.get(l.refUuid));
          return ps > 0 ? { ...l, _sheetScrapForPartCharge: ps } : l;
        }),
        ...sheetScrapLines
      ].map(toEngineLine);
      const payload = buildPreviewPayload(engineLines, opts);
      otherChargesApi.preview(payload)
        .then((data) => {
          if (!alive) return;
          const rows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
          setEngineRows(rows); setEngineErr(false); setOcComputed(true);
        })
        .catch(() => { if (alive) { setEngineRows([]); setEngineErr(true); setOcComputed(true); } });
    }, 400);
    return () => { alive = false; clearTimeout(tid); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineKey, rulesKey, overridesKey, autoApplyDisabled]);

  // Engine amounts aggregated per rule (a rule may emit several rows when it is
  // displayed line-/thickness-wise — sum them for the document figure).
  const engineByRule = useMemo(() => {
    const m = new Map();
    for (const r of engineRows) {
      const id = r.ruleId != null ? String(r.ruleId) : (r.chargeCode || r.chargeName);
      const cur = m.get(id) || { chargeAmount: 0, gstAmount: 0, rate: r.rate, area: 0, quantity: 0 };
      cur.chargeAmount += num(r.chargeAmount);
      cur.gstAmount += num(r.gstAmount);
      // Area = Σ the engine row's real billed area (`area`). The engine returns
      // the true SQM/SQFT for area-based charges (0 for others) — NOT `quantity`,
      // which for grouped thickness-/single-total rows is the line COUNT.
      cur.area += num(r.area);
      // Quantity = Σ the engine row's billed PIECES. For a PER_PCS charge (Logo)
      // this is the real piece count (e.g. Σ toughened pcs), shown instead of area
      // so "pieces × rate = amount" reconciles on screen (the charge is NOT area-based).
      cur.quantity += num(r.quantity);
      m.set(id, cur);
    }
    return m;
  }, [engineRows]);

  const chargeRows = useMemo(() => rules.map((r) => {
    const id = r.id != null ? String(r.id) : (r.chargeCode || r.chargeName);
    const eng = engineByRule.get(id);
    // Area kept at 3 decimals for accuracy (engine carries 4; we round to 3 for display parity with the grid's CHRG AREA).
    return { ...r, _id: id, amount: round2(eng ? eng.chargeAmount : 0), area: round3(eng ? eng.area : 0), quantity: eng ? num(eng.quantity) : 0, rate: eng ? eng.rate : null, applied: !!eng };
  }), [rules, engineByRule]);
  const chargesTotal = useMemo(() => round2(chargeRows.reduce((s, r) => s + num(r.amount), 0)), [chargeRows]);
  // GST of the live auto Other Charges (Σ per-rule engine GST) — needed so the
  // Summary popup can fold OC's tax into Taxable / GST / Grand Total, not just
  // the net amount. Display aggregation only; the OC engine is untouched.
  const chargesGst = useMemo(() => round2(chargeRows.reduce((s, r) => {
    const eng = engineByRule.get(r._id);
    return s + (eng ? num(eng.gstAmount) : 0);
  }, 0)), [chargeRows, engineByRule]);
  // Reconcile the live OC against the base totals with the SAME helper the
  // "Details & Summary" popup uses, so the two running totals are byte-identical
  // and never double-count OC on a reloaded proforma (the old
  // `t.grandTotal + chargesTotal` added persisted OC a second time and dropped
  // OC's GST). `chargesGst` folds the OC tax in; `ocComputed` gates readiness.
  // Pass the document round-off policy so the running total re-derives round-off on
  // the FINAL (base + live OC) total, matching the backend/print rupee instead of
  // leaking the base's pre-OC round-off (the first-load ₹…​.14 vs printed ₹…​.00 bug).
  const roundOffPolicy = useMemo(
    () => ({ roundOffEnabled: header && header.roundOffEnabled, roundOffMode: header && header.roundOffMode }),
    [header && header.roundOffEnabled, header && header.roundOffMode]
  );
  const reconciled = useMemo(
    () => reconcileProformaTotals(t, { amount: chargesTotal, gst: chargesGst, ready: ocComputed }, supplyType, roundOffPolicy),
    [t, chargesTotal, chargesGst, ocComputed, supplyType, roundOffPolicy]
  );
  const runningGrand = reconciled.grandTotal;
  const ocAddedTotal = round2(num(reconciled.liveOtherChargesAdded) + num(reconciled.liveOtherChargesGstAdded));

  // Surface the live auto Other-Charges figure to the parent so the "Details &
  // Summary" popup reflects the same running total shown in this tab. `ready`
  // guards against reconciling before the async engine has produced a result.
  useEffect(() => {
    if (onOtherChargesChange) onOtherChargesChange({ amount: chargesTotal, gst: chargesGst, ready: ocComputed });
  }, [chargesTotal, chargesGst, ocComputed, onOtherChargesChange]);

  const chargeColumns = useMemo(() => [
    {
      title: 'Charge', dataIndex: 'chargeName', key: 'chargeName', ellipsis: true,
      render: (v, r) => (
        <span className="flex items-center gap-1.5">
          <span className="truncate">{v}</span>
          {r.applied ? null : <Tooltip title="Not applied to any line. This rule's applicability (material type / glass type / size / area) doesn't match the current lines — e.g. a free-typed line has no material type. Pick the item from the Item Master, or relax the rule's applicability."><span className="text-[10px] text-slate-400">· n/a</span></Tooltip>}
        </span>
      )
    },
    {
      title: 'By', dataIndex: 'mode', key: 'mode', width: 52, align: 'center',
      onCell: (r) => ({ className: r.mode === 'PERCENT' ? 'pfg-bp-by pct' : 'pfg-bp-by rs' }),
      render: (v) => <span className="font-semibold text-[12px]">{v === 'PERCENT' ? '%' : 'Rs.'}</span>
    },
    {
      // The measure the rate multiplies: PIECES for a PER_PCS charge (Logo counts
      // toughened pcs → its qty, NOT area), else the billed SQM for area-based
      // charges. Shown before the Value/Rs. column, mirroring the Value Added table.
      title: 'Qty / Area', dataIndex: 'area', key: 'area', width: 84, align: 'right',
      render: (_v, r) => (String(r.valueBy || '').toUpperCase() === 'PER_PCS'
        // Piece count (whole number, Indian grouping) so "pcs × rate = amount".
        ? num(r.quantity).toLocaleString('en-IN')
        // Area-based charges keep 3-dp SQM for parity with the grid's CHRG AREA column.
        : num(r.area).toFixed(3))
    },
    {
      title: 'Value', key: 'value', width: 108, align: 'right',
      // Stage 6CM.2 — lock the editable runtime value when Company Defaults
      // disables runtime overrides (backend also enforces this server-side).
      onCell: () => ({ className: (ro || overrideLocked) ? undefined : 'pfg-bp-edit' }),
      render: (_v, r) => {
        const ov = chargeValues[r._id];
        const val = ov != null && ov !== '' ? num(ov) : num(r.ruleValue);
        return (
          <Tooltip title={overrideLocked ? 'Runtime override is disabled in Company Defaults.' : ''}>
            <InputNumber
              size="small" min={0} controls={false} variant="borderless" disabled={ro || overrideLocked}
              prefix={r.mode === 'INR' ? '₹' : (r.mode === 'MULTIPLIER' ? '×' : null)}
              formatter={(fv) => (r.mode === 'PERCENT' && fv != null && fv !== '' ? `${fv}%` : `${fv == null ? '' : fv}`)}
              parser={(pv) => String(pv == null ? '' : pv).replace(/[%₹×,\s]/g, '')}
              value={val} onChange={(nv) => setChargeValue(r._id, nv)} className="pfg-bp-num" />
          </Tooltip>
        );
      }
    },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, align: 'right', render: (v) => (num(v) ? num(v).toFixed(2) : '0.00') }
  ], [chargeValues, ro, overrideLocked, setChargeValue]);

  // ── Manual ad-hoc charges (document-level, NO GST) ──
  // Free-form "additional cost" rows: Description + Area + Rs → Amount = Area×Rs
  // (blank Area = 1, i.e. flat lump-sum). Stored on header.manualOtherCharges and
  // folded into the running grand total by computeProformaPreview (post-tax).
  const manualCharges = useMemo(
    () => ((header && Array.isArray(header.manualOtherCharges)) ? header.manualOtherCharges : []),
    [header]
  );
  // Manual Charge Master (catalogue) — the Description is picked from here; the
  // chosen entry's default Rs pre-fills the rate (still editable per document).
  //
  // Stage 6PL-1d — the catalogue now rides in the proforma's own dropdown
  // payload, already overlaid with THIS customer's negotiated rates. Fetching it
  // separately would mean two sources that can disagree about which customer
  // they belong to. The master's own endpoint stays the fallback for the case
  // where the payload has not arrived (or an older server does not send it), so
  // the picker is never empty.
  const [manualMasters, setManualMasters] = useState([]);
  const ddManualCharges = (dropdowns && Array.isArray(dropdowns.manualCharges)) ? dropdowns.manualCharges : null;
  useEffect(() => {
    if (ddManualCharges && ddManualCharges.length) { setManualMasters(ddManualCharges); return undefined; }
    let alive = true;
    loadManualChargeLookupsSafe().then((list) => { if (alive) setManualMasters(list); });
    return () => { alive = false; };
  }, [ddManualCharges]);
  const manualOptions = useMemo(
    () => manualMasters.map((m) => ({
      value: m.chargeName, label: m.chargeName,
      rate: num(m.defaultRate), gstRate: num(m.gstRate),
      // Provenance, carried onto the picked row so the document can later say
      // where the rate came from. Null when no price list covers this charge.
      manualChargeId: m.id || null,
      priceListId: m.priceListId || null,
      priceSource: m.priceSource || null
    })),
    [manualMasters]
  );
  const setManual = useCallback((next) => {
    setHeader((h) => ({ ...h, manualOtherCharges: typeof next === 'function' ? next(h.manualOtherCharges || []) : next }));
  }, [setHeader]);
  const addManualRow = useCallback(() => setManual((list) => [...list, { label: '', area: null, rate: null, gstRate: null }]), [setManual]);
  const updateManualRow = useCallback((idx, patch) => setManual((list) => list.map((r, i) => (i === idx ? { ...r, ...patch } : r))), [setManual]);
  const removeManualRow = useCallback((idx) => setManual((list) => list.filter((_, i) => i !== idx)), [setManual]);
  // Pick a master charge: set the description and pre-fill its default Rs + GST %.
  const pickManualCharge = useCallback((idx, value) => {
    const opt = manualOptions.find((o) => o.value === value);
    updateManualRow(idx, {
      label: value || '', rate: opt ? opt.rate : null, gstRate: opt ? opt.gstRate : null,
      // Stage 6PL-1d — which catalogue entry, and which price list (if any) set
      // this rate. A MASTER stamp is not written here: the row simply carries no
      // list, which is what "the master priced it" looks like.
      manualChargeId: opt ? opt.manualChargeId : null,
      priceListId: opt ? opt.priceListId : null,
      priceSource: opt ? opt.priceSource : null
    });
  }, [manualOptions, updateManualRow]);
  // Amount = Area × Rs (blank Area = 1). GST is taken from the picked master
  // entry and computed into the document totals — it is NOT shown per-row here,
  // matching the Fabrication / Other Charges grids (no per-row GST column).
  const manualAmountOf = (m) => round2((num(m.area) > 0 ? num(m.area) : 1) * num(m.rate));
  const manualTotal = useMemo(() => round2(manualCharges.reduce((s, m) => s + manualAmountOf(m), 0)), [manualCharges]);

  const otherChargesTab = (
    <div className="flex flex-col lg:flex-row gap-3">
      {/* Fabrication + Value Added Services (stacked) */}
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        <div>
          <div className="pfg-bp-cap">Fabrication</div>
          <Table
            size="small" bordered rowKey="key" columns={fabColumns} dataSource={fabRows}
            pagination={false} scroll={{ y: 152 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No active fabrications" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><span className="font-semibold text-slate-600">Total</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right"><span className="font-semibold text-slate-800 tabular-nums">{fabTotal.toFixed(2)}</span></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>

        {/* Value Added Services — process #tag charges, read-only (below Fabrication) */}
        <div>
          <div className="pfg-bp-cap">Value Added Services <span className="text-slate-400 font-normal">· process charges</span></div>
          <Table
            size="small" bordered rowKey="key" columns={vasColumns} dataSource={vas.rows}
            pagination={false} scroll={{ y: 152 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No process charges" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}><span className="font-semibold text-slate-600">Value Add (Process)</span></Table.Summary.Cell>
                  {/* Use the canonical document total (same figure the Summary tab shows) so the
                      two "Value Add (Process)" numbers can never disagree by a rounding cent. */}
                  <Table.Summary.Cell index={4} align="right"><span className="font-semibold text-indigo-700 tabular-nums">{num(t.valueAddedAmount).toFixed(2)}</span></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>
      </div>

      {/* Other-Charges (rule-engine computed) */}
      <div className="flex-1 min-w-0">
        <div className="pfg-bp-cap">Other Charges <span className="text-slate-400 font-normal">· rule engine{engineErr ? ' · offline' : ''}</span></div>
        {/* Stage 6CM.2 — Company Defaults gates (informational). */}
        {autoApplyDisabled ? (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
            Auto Other Charges is disabled in Company Defaults.
          </div>
        ) : null}
        {overrideLocked ? (
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1 mb-1">
            Runtime override is disabled in Company Defaults.
          </div>
        ) : null}
        <Table
          size="small" bordered rowKey="_id" columns={chargeColumns} dataSource={chargeRows}
          pagination={false} scroll={{ y: 152 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={rulesErr ? 'Could not load charges' : 'No charge rules configured'} /> }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}><span className="font-semibold text-slate-600">Charges total</span></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><span className="font-semibold text-amber-700 tabular-nums">{chargesTotal.toFixed(2)}</span></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        {/* Manual ad-hoc charges — document-level "additional cost" (no GST). */}
        <div className="mt-2">
          <div className="pfg-bp-cap flex items-center justify-between">
            <span>Manual Charges <span className="text-slate-400 font-normal">· additional cost · GST from master</span></span>
            {!ro ? (
              <Button size="small" type="link" icon={<PlusOutlined />} onClick={addManualRow} className="!px-1 !h-5 text-[11px]">Add</Button>
            ) : null}
          </div>
          {manualCharges.length === 0 ? (
            <div className="text-[11px] text-slate-400 px-1 py-1">
              No manual charges. Use “Add” to pick a charge from the Manual Charge Master. Amount = Area × Rs; GST comes from the master.
              {manualMasters.length === 0 ? <span className="text-amber-600"> · No charges configured yet — add them in Masters → Manual Charge Master.</span> : null}
            </div>
          ) : (
            <table className="w-full text-[12px] border border-slate-200 rounded">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="text-left font-medium px-2 py-1">Description</th>
                  <th className="text-right font-medium px-1 py-1" style={{ width: 88 }}>Area</th>
                  <th className="text-right font-medium px-1 py-1" style={{ width: 96 }}>Rs</th>
                  <th className="text-right font-medium px-2 py-1" style={{ width: 110 }}>Amount</th>
                  <th style={{ width: 28 }} />
                </tr>
              </thead>
              <tbody>
                {manualCharges.map((m, idx) => (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-1">
                      <Select size="small" variant="borderless" className="w-full" showSearch optionFilterProp="label"
                        value={m.label || undefined} disabled={ro} placeholder="Pick charge…"
                        options={manualOptions} onChange={(v) => pickManualCharge(idx, v)}
                        notFoundContent={manualMasters.length === 0 ? 'No master charges' : 'No match'} />
                    </td>
                    <td className="px-1">
                      <InputNumber size="small" min={0} controls={false} variant="borderless" className="pfg-bp-num w-full"
                        value={m.area} disabled={ro} placeholder="—" onChange={(v) => updateManualRow(idx, { area: v })} />
                    </td>
                    <td className="px-1">
                      <InputNumber size="small" min={0} controls={false} variant="borderless" prefix="₹" className="pfg-bp-num w-full"
                        value={m.rate} disabled={ro} placeholder="0"
                        onChange={(v) => updateManualRow(idx, {
                          rate: v,
                          // Stage 6PL-1d — a hand-typed rate is the operator's own
                          // decision; it drops the price list's stamp so any future
                          // automatic re-pricing leaves it alone.
                          priceSource: 'MANUAL', priceListId: null
                        })} />
                    </td>
                    <td className="px-2 text-right tabular-nums text-slate-700">{manualAmountOf(m).toFixed(2)}</td>
                    <td className="text-center">
                      {!ro ? (
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeManualRow(idx)} className="!h-5 !w-5 !p-0" />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="px-2 py-1 font-semibold text-slate-600" colSpan={3}>Manual total</td>
                  <td className="px-2 py-1 text-right font-semibold text-amber-700 tabular-nums">{manualTotal.toFixed(2)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Running grand total (document grand total + engine charges). */}
        <div className="mt-2 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div className="text-[11px] text-slate-500">
            Grand total <span className="text-slate-400">{formatMoney(t.grandTotal)}</span>
            {ocAddedTotal ? <span className="text-amber-600"> + charges {formatMoney(ocAddedTotal)}</span> : null}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-emerald-600">Running total</div>
            <div className="text-[16px] font-bold text-emerald-700 tabular-nums">{formatMoney(runningGrand)}</div>
          </div>
        </div>
      </div>
    </div>
  );

  // The financial summary + editable document discount moved into the
  // "Details & Summary" popup (ProformaSummaryModal). This panel now hosts
  // Other Charges + the two Remarks tabs only.

  // Free-text remarks saved with the proforma. Bound straight to the header via
  // setHeader (same pattern as every other control); disabled when CONFIRMED /
  // CANCELLED (ro). Persisted through buildSavePayload → proforma_remarks /
  // work_order_remarks columns.
  const remarksTab = (field, placeholder) => (
    <Input.TextArea
      value={(header && header[field]) || ''}
      onChange={(e) => setHeader((h) => ({ ...h, [field]: e.target.value }))}
      disabled={ro}
      placeholder={placeholder}
      autoSize={{ minRows: 4, maxRows: 10 }}
      maxLength={5000}
      showCount
    />
  );

  return (
    <div className="pfg-bottom-panel">
      <style>{`
.pfg-bottom-panel{background:#fff;border:1px solid #cbd5e1;border-radius:6px;box-shadow:0 1px 2px rgba(15,23,42,.04);}
.pfg-bottom-panel .ant-tabs-nav{margin:0;padding:0 10px;background:#f8fafc;border-bottom:1px solid #e2e8f0;border-radius:6px 6px 0 0;}
.pfg-bottom-panel .ant-tabs-tab{padding:7px 12px!important;font-size:12.5px!important;}
.pfg-bottom-panel .ant-tabs-content-holder{padding:10px 12px;}
.pfg-bp-cap{font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#64748b;margin-bottom:4px;}
/* Excel-tight grid: thin gridlines, short rows, no wasted width. */
.pfg-bottom-panel .ant-table-thead>tr>th{background:#eef2f7;color:#475569;font-size:10px;font-weight:700;letter-spacing:.03em;padding:3px 8px!important;text-transform:uppercase;border-color:#d4dbe3!important;}
.pfg-bottom-panel .ant-table-tbody>tr>td{font-size:12px;padding:0 8px!important;height:26px;border-color:#e3e8ee!important;color:#1e293b;}
.pfg-bottom-panel .ant-table-tbody>tr:hover>td{background:#f6faff;}
.pfg-bottom-panel .ant-table-summary>tr>td{font-size:12px;padding:4px 8px!important;background:#f1f5f9;border-color:#d4dbe3!important;}
/* Editable cells fill the whole cell (borderless), faint editable tint. */
.pfg-bottom-panel td.pfg-bp-edit{padding:0!important;background:#fffdf3;}
.pfg-bottom-panel td.pfg-bp-edit:hover{background:#fff7df;}
.pfg-bottom-panel td.pfg-bp-edit:focus-within{box-shadow:inset 0 0 0 2px #2563eb;background:#fff;}
.pfg-bottom-panel .pfg-bp-num{width:100%;height:25px;background:transparent;}
.pfg-bottom-panel .pfg-bp-num.ant-input-number-affix-wrapper{padding:0 8px;}
.pfg-bottom-panel .pfg-bp-num .ant-input-number-input{height:25px;font-size:12px;text-align:right;padding:0;font-variant-numeric:tabular-nums;}
.pfg-bottom-panel .pfg-bp-num .ant-input-number-prefix{color:#94a3b8;margin-inline-end:2px;}
/* BY column tint (Rs. blue, % amber) like the reference. */
.pfg-bottom-panel td.pfg-bp-by{padding:0!important;}
.pfg-bottom-panel td.pfg-bp-by.rs{background:#eaf2ff;color:#1e40af;}
.pfg-bottom-panel td.pfg-bp-by.pct{background:#fff7e6;color:#b45309;}
.pfg-bottom-panel .ant-descriptions-item-label{font-size:11.5px;color:#64748b;background:#f8fafc;padding:4px 10px!important;}
.pfg-bottom-panel .ant-descriptions-item-content{font-size:12px;color:#0f172a;padding:4px 10px!important;}
      `}</style>
      <Tabs
        size="small"
        items={[
          { key: 'charges', label: 'Other Charges', children: otherChargesTab },
          { key: 'proformaRemarks', label: 'Proforma Remarks', children: remarksTab('proformaRemarks', 'Remarks that apply to this proforma…') },
          { key: 'workOrderRemarks', label: 'WorkOrder Remarks', children: remarksTab('workOrderRemarks', 'Remarks / instructions for the work order…') }
        ]}
      />
    </div>
  );
}
